import type { HandlerIds } from "./ApiSubscriber";
import type { Subject, SubjectScheme } from "./context";

export type Param = string | number;
export type Params = Record<string, Param>;

export const BACKEND_ROLE = "backend";

const V1_PREFIX = "pip.${companyId}.${userId}.";
const V2_PREFIX = "pip.${companyId}.${userId}.${role}.";

/** The legacy wire shape of a v2 template: the role token dropped. */
export function legacyV1Template(template: string): string {
  return template.startsWith(V2_PREFIX) ? V1_PREFIX + template.slice(V2_PREFIX.length) : template;
}

/** The subject as this session's scheme puts it on the wire. */
export function wireSubject<P extends Params>(
  subject: Subject<P>,
  scheme: SubjectScheme | undefined,
): Subject<P> {
  return scheme === "v2" ? subject : { ...subject, template: legacyV1Template(subject.template) };
}

export function withoutRole(vars: Record<string, string>): Record<string, string> {
  const { role: _role, ...rest } = vars;
  return rest;
}

export function resolveSubject(subject: Subject, baseVars: Params, allowWildcards = false): string {
  let resolved = subject.template;
  const params = { ...subject.variables, ...baseVars };
  for (const param in params) {
    const v = "${" + param + "}";
    const pos = resolved.indexOf(v);
    if (pos < 0) {
      throw Error(`Subject ${subject.template} does not contain ${param} param`);
    }
    resolved = resolved.slice(0, pos) + params[param] + resolved.slice(pos + v.length);
  }
  if (!allowWildcards) {
    const unresolved = getSubjectVarNames(resolved);
    if (unresolved.length > 0) {
      throw Error(`Subject ${subject.template} has unresolved variables: ${unresolved.join(", ")}`);
    }
  }
  return resolved.replaceAll(/\$\{.*?}/g, "*");
}

export function extractVarsAndIds<P extends Params>(
  subject: Subject<P>,
  resolved: string,
): [P, HandlerIds] {
  const subjectVars = Object.entries(extractSubjectVars(subject.template, resolved));
  const [companyEntry, userEntry, third] = subjectVars;
  if (companyEntry?.[0] !== "companyId" || userEntry?.[0] !== "userId") {
    throw Error(`missing companyId and/or userId: ${subject.template}`);
  }
  const hasRole = third?.[0] === "role";
  const params = Object.fromEntries(
    subjectVars.slice(hasRole ? 3 : 2).map(([name, value]) => [name, subject.types![name](value)]),
  );
  const ids: HandlerIds = { companyId: companyEntry[1], userId: userEntry[1] };
  if (hasRole) {
    ids.role = third[1];
  }
  return [params as P, ids];
}

function extractSubjectVars(template: string, subject: string): Record<string, string> {
  const templateTokens = template.split(".");
  const subjectTokens = subject.split(".");

  if (templateTokens.length !== subjectTokens.length) {
    throw new Error(`subjects length don't match: ${template} <-> ${subject}`);
  }
  const varNames = getSubjectVarNames(template);
  const subjectVars: Record<string, string> = {};
  templateTokens.forEach((token, i) => {
    if (token.startsWith("${")) {
      token = token.slice(2, -1);
      if (varNames.includes(token)) {
        subjectVars[token] = subjectTokens[i];
      }
    }
  });
  return subjectVars;
}

const VAR_PATTERN = /\$\{(.*?)}/g;

function getSubjectVarNames(subject: string): string[] {
  return [...subject.matchAll(VAR_PATTERN)].map((m) => m[1]);
}
